import { Box, Txt } from '../ui/primitives';
import { Icon } from '../ui/Icon';
import { dateTime, duration, num, percent } from '../utils/format';
import { type DateRange, type ResolveContext, rangeLabel, rangeSubLabel } from '../utils/dateRange';
import type { Ageing, Kpis } from '../api/types';

/**
 * Pending Pickup focus panel.
 *
 * The dashboard's headline question is "how many orders are still waiting for
 * pickup?", so this answers it with a hero figure (proportional figures, 56px)
 * plus a meter showing the share of the day. The meter is a single ratio
 * against a limit, so it uses one track and one fill rather than a pie.
 */
export interface PendingPickupPanelProps {
  kpis: Kpis;
  ageing: Ageing | null;
  range: DateRange;
  rangeCtx: ResolveContext;
  pendingStatusLabels: string[];
}

export function PendingPickupPanel({
  kpis,
  ageing,
  range,
  rangeCtx,
  pendingStatusLabels,
}: PendingPickupPanelProps): JSX.Element {
  const { pendingPickup, total, pendingPickupPercent } = kpis;
  const share = Math.max(0, Math.min(100, pendingPickupPercent));
  const clear = total > 0 && pendingPickup === 0;

  return (
    <Box
      as="section"
      aria-label="Pending pickup overview"
      className="rounded-card bg-surface-card ring-1 ring-hairline shadow-card p-5 sm:p-6 flex flex-col"
    >
      <Box className="flex items-start justify-between gap-3">
        <Box>
          <Txt as="h2" className="text-[15px] font-semibold text-ink-primary">
            Pending Pickup Overview
          </Txt>
          <Txt as="p" className="mt-0.5 text-[12px] text-ink-muted">
            {rangeLabel(range, rangeCtx)}
            {range.from !== range.to ? (
              <Txt className="text-ink-muted"> · {rangeSubLabel(range)}</Txt>
            ) : null}
          </Txt>
        </Box>
        {total > 0 ? (
          <Box className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface-sunken ring-1 ring-hairline shrink-0">
            <Box
              className="w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: clear ? 'var(--life-delivered)' : 'var(--life-pending)' }}
              aria-hidden
            />
            <Txt className="text-[11px] font-medium text-ink-secondary whitespace-nowrap">
              {clear ? 'All collected' : 'Action needed'}
            </Txt>
          </Box>
        ) : null}
      </Box>

      {/* Hero figure */}
      <Box className="mt-5 flex items-end gap-3">
        <Txt as="p" className="text-[56px] leading-[0.95] font-semibold text-ink-primary">
          {num(pendingPickup)}
        </Txt>
        <Txt as="p" className="pb-2 text-[13px] text-ink-secondary">
          still awaiting
          <br />
          courier pickup
        </Txt>
      </Box>

      <Txt as="p" className="mt-2 text-[13px] text-ink-secondary tabular">
        {num(pendingPickup)} / {num(total)} total orders
        <Txt className="text-ink-muted"> &middot; </Txt>
        <Txt className="font-semibold text-ink-primary">{percent(pendingPickupPercent)}</Txt> pending
      </Txt>

      {/* Meter: one ratio against the day's total */}
      <Box className="mt-3">
        <Box
          className="h-2.5 w-full rounded-full overflow-hidden"
          style={{ backgroundColor: 'var(--surface-sunken)' }}
          role="img"
          aria-label={`${percent(pendingPickupPercent)} of orders pending pickup`}
        >
          <Box
            className="h-full rounded-full transition-[width] duration-500 ease-out"
            style={{
              width: `${share}%`,
              backgroundColor: clear ? 'var(--life-delivered)' : 'var(--life-pending)',
              minWidth: pendingPickup > 0 ? 6 : 0,
            }}
          />
        </Box>
        <Box className="mt-1.5 flex justify-between">
          <Txt className="text-[11px] text-ink-muted">0%</Txt>
          <Txt className="text-[11px] text-ink-muted">100% of {num(total)}</Txt>
        </Box>
      </Box>

      {/* Counted statuses: the number above is never a mystery */}
      <Box className="mt-4 flex flex-wrap items-center gap-1.5">
        <Txt className="text-[11px] text-ink-muted">Counts</Txt>
        {pendingStatusLabels.map((label) => (
          <Txt
            key={label}
            className="px-2 py-0.5 rounded-md text-[11px] font-medium text-ink-secondary bg-surface-sunken ring-1 ring-hairline"
          >
            {label}
          </Txt>
        ))}
      </Box>

      {/* Ageing */}
      <Box className="mt-5 pt-4 border-t border-hairline">
        {clear ? (
          <Box
            className="flex items-center gap-2 rounded-xl px-3 py-2.5 ring-1 ring-hairline"
            style={{ backgroundColor: 'var(--surface-sunken)' }}
          >
            <Box style={{ color: 'var(--life-delivered)' }}>
              <Icon name="check" className="w-4 h-4" strokeWidth={2.25} />
            </Box>
            <Txt className="text-[13px] font-medium text-ink-primary">
              All orders in this range have been picked up.
            </Txt>
          </Box>
        ) : ageing && pendingPickup > 0 ? (
          <Box className="grid grid-cols-1 sm:grid-cols-3 gap-px rounded-xl overflow-hidden bg-hairline ring-1 ring-hairline">
            <AgeingCell label="Oldest pending" value={dateTime(ageing.oldestOrderDate)} sub={ageing.oldestOrderNo ?? undefined} />
            <AgeingCell label="Latest pending" value={dateTime(ageing.latestOrderDate)} />
            <AgeingCell label="Avg. waiting" value={duration(ageing.averagePendingHours)} />
          </Box>
        ) : (
          <Txt as="p" className="text-[12px] text-ink-muted">
            No pending orders to age for this range.
          </Txt>
        )}
      </Box>
    </Box>
  );
}

function AgeingCell({ label, value, sub }: { label: string; value: string; sub?: string }): JSX.Element {
  return (
    <Box className="bg-surface-card px-3 py-2.5">
      <Txt as="p" className="text-[11px] text-ink-muted">
        {label}
      </Txt>
      <Txt as="p" className="mt-0.5 text-[13px] font-semibold text-ink-primary tabular">
        {value}
      </Txt>
      {sub ? (
        <Txt as="p" className="mt-0.5 text-[11px] text-ink-muted truncate tabular">
          {sub}
        </Txt>
      ) : null}
    </Box>
  );
}
